import type { Position, PointItem } from '../../types.js';
import { useAddress } from '../../hooks/useAddress';
import { useState } from 'react';
import { Delete } from '@mui/icons-material';
import { LocationSearch } from '../shared/LocationSearch';
import './RoutePoint.css';

const getPointIdentifier = (type: 'start' | 'via' | 'end', viaIndex?: number): string => {
  switch (type) {
    case 'start':
      return 'S';
    case 'end':
      return 'E';
    case 'via':
      return `${viaIndex !== undefined ? viaIndex + 1 : ''}`;
    default:
      return '';
  }
};

const formatAddress = (address: string): { primary: React.ReactNode; secondary: string } => {
  if (!address) return { primary: 'Keine Adresse', secondary: '' };
  
  const parts = address.split(',').map(part => part.trim());
  
  if (parts.length === 1) {
    return { primary: parts[0], secondary: '' };
  } else if (parts.length >= 2) {
    return { 
      primary: parts[0], 
      secondary: parts.slice(1).join(', ') 
    };
  }
  
  return { primary: address, secondary: '' };
};

interface RoutePointProps {
  type: 'start' | 'via' | 'end';
  position: Position | null; // null for deleted markers during animation
  onDelete: () => void;
  onEdit?: (newPosition: { lat: number; lon: number }, displayName: string) => void;
  onManualGeocode?: (pointId: string, position: { lat: number; lon: number }) => void;
  isGeocodingLoading?: boolean;
  dragHandleProps?: any;
  viaIndex?: number; // The 1-based index for via points (Via 1, Via 2, etc.)
  address?: string; // Pre-resolved address to avoid duplicate geocoding
  allPoints?: PointItem[]; // All points in the route (used for optimization)
  pointIndex?: number; // Index of this point in the allPoints array
  point: PointItem; // The actual point object for context
  routeSourceType?: 'manual' | 'gpx_import' | 'gpx_optimized'; // Route source type for geocoding decisions
}

export const RoutePoint = ({ type, position, onDelete, onEdit, onManualGeocode, isGeocodingLoading, dragHandleProps, viaIndex, address: preResolvedAddress, allPoints, pointIndex, point, routeSourceType }: RoutePointProps) => {
  const identifier = getPointIdentifier(type, viaIndex);
  
  const isPlaceholder = position && position.lat === 0 && position.lng === 0 && !preResolvedAddress;
  
  // Detect if geocoding should be skipped for this point
  const shouldSkipGeocoding = (() => {
    if (isPlaceholder || pointIndex === undefined || preResolvedAddress) {
      return false; // Don't skip if we have address or it's a placeholder
    }
    
    // Never skip geocoding for manually added points
    if (point?.manuallyAdded) {
      return false;
    }
    
    const isStartOrEnd = pointIndex === 0 || (allPoints && pointIndex === allPoints.length - 1);
    if (isStartOrEnd) {
      return false; // Never skip start/end points
    }
    
    // Skip geocoding for middle points of uploaded routes
    if (routeSourceType === 'gpx_import' || routeSourceType === 'gpx_optimized') {
      return true; // Skip middle points of uploaded routes
    }
    
    return false; // Don't skip for manual routes
  })();
  
  const { address } = useAddress(isPlaceholder ? null : position, preResolvedAddress, shouldSkipGeocoding);
  const [isEditOpen, setIsEditOpen] = useState(false);

  // Check if we're showing coordinates (either no address resolved, or address is coordinates)
  const isShowingCoordinates = !address || /^\d+\.\d+,\s*\d+\.\d+$/.test(address);

  // Get formatted address using similar logic as locationSearch
  const getFormattedAddress = (): { primary: React.ReactNode; secondary: string } => {
    if (!position) {
      return { primary: 'Keine Position', secondary: '' };
    }
    
    // For placeholder points, show loading text while waiting for new location
    if (isPlaceholder) {
      return { primary: 'Lade neue Adresse...', secondary: 'Standort wird gesucht...' };
    }
    
    // If geocoding is in progress, show loading state
    if (isGeocodingLoading) {
      return { 
        primary: 'Resolving address...', 
        secondary: `${position.lat.toFixed(4)}, ${position.lng.toFixed(4)}`
      };
    }
    
    // If we have address string, format it properly
    if (address && !isShowingCoordinates) {
      return formatAddress(address);
    }
    
    // Fallback to coordinates - show "Unresolved address" in primary, coordinates in secondary
    return { 
      primary: <em className="unresolved-address">Unresolved address</em>,
      secondary: `${position.lat.toFixed(4)}, ${position.lng.toFixed(4)}`
    };
  };

  const { primary, secondary } = getFormattedAddress();

  const handleEdit = () => {
    setIsEditOpen(true);
  };

  const handleLocationSelect = (location: { lat: number; lon: number; displayName: string }) => {
    if (onEdit) {
      onEdit({ lat: location.lat, lon: location.lon }, location.displayName);
    }
    setIsEditOpen(false);
  };

  const handleEditClose = () => {
    setIsEditOpen(false);
  };
  
  return (
    <>
      <div className="route-point-wrapper">
        {/* Drag Handle - moved to left */}
        <div 
          className="route-point-handle"
          {...dragHandleProps}
        >
        </div>

        {/* Point Type Indicator */}
        <div className={`route-point-type ${type}`}>
          {identifier}
        </div>

        {/* Address Content - Two-line design */}
        <div className="route-point-content">
          <div className={`route-point-primary ${isPlaceholder ? 'placeholder-dots' : ''} ${isGeocodingLoading ? 'geocoding-loading' : ''}`}>
            {/* Show geocoding trigger icon before "Unresolved address" (not when loading) */}
            {position && !isPlaceholder && !isGeocodingLoading && onManualGeocode && isShowingCoordinates && (
              <button
                className="route-point-geocode-trigger"
                onClick={() => onManualGeocode(point.id, { lat: position.lat, lon: position.lng })}
                aria-label="Resolve address"
                title="Get address for this location"
              >
                <svg viewBox="0 0 24 24" fill="none" width="14" height="14">
                  <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                  <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            <span className="route-point-primary-text">{primary}</span>
          </div>
          {/* Always show secondary line - either address details or coordinates */}
          {secondary && (
            <div className={`route-point-secondary ${isPlaceholder ? 'placeholder-dots' : ''} ${isGeocodingLoading ? 'geocoding-loading' : ''}`}>
              {secondary}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="route-point-actions">
          {/* Edit Button */}
          {onEdit && (
            <button 
              className="route-point-edit"
              onClick={handleEdit}
              aria-label="Edit point"
              title="Edit point"
            >
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
          
          {/* Delete Button */}
          <button 
            className="route-point-delete"
            onClick={onDelete}
            aria-label="Delete point"
            title="Delete point"
          >
            <Delete />
          </button>
        </div>
      </div>

      {/* Location Search Modal */}
      {isEditOpen && (
        <LocationSearch
          isOpen={isEditOpen}
          onClose={handleEditClose}
          onLocationSelect={handleLocationSelect}
          placeholder="Neuen Standort suchen..."
          initialQuery={address || ''}
          className="route-point-location-search"
        />
      )}
    </>
  );
};
